/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { Button, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import { ICittaEnquireFormData } from './citta-enquire-form.data';
import { ICittaEnquireFormProps } from './citta-enquire-form.props.autogenerated';
import { submitPdpEnquiryFormAction, SubmitPdpEnquiryFormDataActionInput } from '../../data-actions/pdp-enquiry/submit-pdp-enquiry-form-data';

export interface ICittaEnquireFormViewProps extends ICittaEnquireFormProps<ICittaEnquireFormData> {
    ModalItem: React.ReactNode;
    EnquireButton: React.ReactNode;
}

interface ICittaEnquireFormState {
    isOpen: boolean;
    responseReceived: boolean;
    design: string;
    name: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    storePickerValue: string;
    storePickerText: string;
    enquiryMessage: string;
    upholstery: string;
    dError: boolean;
    nError: boolean;
    lNError: boolean;
    eAError: boolean;
    pHError: boolean;
    pCError: boolean;
    reqError: boolean;
    eAValError: boolean;
    enquiryMessageError: boolean;
    cHError: boolean;
    nLSError: boolean;
}

/**
 *
 * CittaEnquireForm component
 * @extends {React.PureComponent<ICittaEnquireFormProps<ICittaEnquireFormData>>}
 */
class CittaEnquireForm extends React.PureComponent<ICittaEnquireFormProps<ICittaEnquireFormData>, ICittaEnquireFormState> {
    private response: boolean = false;

    private _designInputRef: React.RefObject<HTMLInputElement>;
    private _nameInputRef: React.RefObject<HTMLInputElement>;
    private _lastNameInputRef: React.RefObject<HTMLInputElement>;
    private _emailAddressInputRef: React.RefObject<HTMLInputElement>;
    private _phoneNumberInputRef: React.RefObject<HTMLInputElement>;
    private _enquiryMessageInputRef: React.RefObject<HTMLTextAreaElement>;
    private _productTypePickerSelectRef: React.RefObject<HTMLSelectElement>;
    private _storePickerSelectRef: React.RefObject<HTMLSelectElement>;
    private _upholsteryInputRef: React.RefObject<HTMLInputElement>;
    private requiredErrorMessage: string | undefined;
    private eAValidationErrorMessage: string | undefined;

    private designValue: boolean = false;
    private nameValue: boolean = false;
    private lastNameValue: boolean = false;
    private emailAddressValue: boolean = false;
    private phoneNumberValue: boolean = false;
    private formInputValues: boolean = false;

    constructor(props: ICittaEnquireFormProps<ICittaEnquireFormData>) {
        super(props);

        this.state = {
            isOpen: false,
            responseReceived: false,
            design: props.config.productDesign || '',
            name: '',
            lastName: '',
            emailAddress: '',
            phoneNumber: '',
            enquiryMessage: '',
            storePickerValue: '',
            storePickerText: '',
            upholstery: '',
            dError: false,
            cHError: false,
            lNError: false,
            eAError: false,
            pHError: false,
            nError: false,
            reqError: false,
            pCError: false,
            eAValError: false,
            enquiryMessageError: false,
            nLSError: false
        }

        this._designInputRef = React.createRef();
        this._nameInputRef = React.createRef();
        this._lastNameInputRef = React.createRef();
        this._emailAddressInputRef = React.createRef();
        this._phoneNumberInputRef = React.createRef();
        this._enquiryMessageInputRef = React.createRef();
        this._productTypePickerSelectRef = React.createRef();
        this._storePickerSelectRef = React.createRef();
        this._upholsteryInputRef = React.createRef();

        this._closeModal = this._closeModal.bind(this);
        this._openModal = this._openModal.bind(this);
        this._handleSend = this._handleSend.bind(this);
    }
    
    public render(): JSX.Element | null {
        const viewProps: ICittaEnquireFormViewProps = {
            ... this.props,
            ModalItem: this._renderEnquiremodal(),
            EnquireButton: this._renderEnquireButton()
        };
        return this.props.renderView(viewProps) as React.ReactElement;
    }


    private _closeModal(): void {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen
        }));
    }
    private _openModal(): void {
        this.setState(prevState => ({
            isOpen: !prevState.isOpen,
            responseReceived: false
        }));
    }

    private _renderEnquireButton(): JSX.Element {
        return (
            <Button 
                color={'primary'}
                className="citta-enquire-button"
                onClick={()=> this._openModal()}
            >
                {this.props.config.buttonText || 'Enquire Now'}
            </Button>
        )
    }

    private _renderAlert(className: string): JSX.Element {
        const inputBoxErrors = (this.state.nError || this.state.lNError || this.state.eAError || this.state.pHError || this.state.cHError || this.state.enquiryMessageError);
        if (inputBoxErrors) {
            this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
        }
        if (this.state.emailAddress !== '' && this.state.eAValError) {
            this.eAValidationErrorMessage = this.props.resources.eAValidationErrorMessage;
        }

        const reqError = this.state.reqError;
        const eAValError = this.state.eAValError;
        return (
            <>
                {reqError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.requiredErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.requiredErrorMessage}
                            </>
                        }
                    </span>
                )}
                {eAValError && (
                    <span className={`${className}__alert`} role='alert' aria-live='assertive'>
                        {
                            this.eAValidationErrorMessage &&
                            <>
                                <span className={`${className}__alert-icon`} aria-hidden='true' />
                                {this.eAValidationErrorMessage}
                            </>
                        }
                    </span>
                )}
            </>
        );
    }

    private handleChange = (inputText: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        switch (inputText) {
            case 'designInput': {
                this.setState({ design: value, reqError: false, dError: false, eAValError: false, nLSError: false });
                break;
            }
            case 'nameInput': {
                this.setState({ name: value, reqError: false, nError: false, eAValError: false, nLSError: false });
                break;
            }
            case 'lastNameInput': {
                this.setState({ lastName: value, reqError: false, lNError: false, eAValError: false, nLSError: false });
                break;
            }
            case 'emailAddress': {
                this.setState({ emailAddress: value, reqError: false, eAError: false, eAValError: false, nLSError: false });
                break;
            }
            case 'phoneNumber': {
                this.setState({ phoneNumber: value, reqError: false, pHError: false, eAValError: false, nLSError: false });
                break;
            }
            case 'upholstery': {
                this.setState({ upholstery: value, reqError: false, eAValError: false, nLSError: false });
                break;
            }
            default: null;
        }
    }

    private onChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const text = e.target.options[e.target.options.selectedIndex].textContent;
        this.setState({ storePickerValue: e.target.value || '', storePickerText: text || '' });
    };

    private isEmailValid = (email: string): boolean => {
        const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(email);
    };

    private handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        this.setState({ enquiryMessage: value, reqError: false, eAValError: false, nLSError: false });
    }

    private _renderEnquiremodal(): JSX.Element {
        const { config, resources } = this.props;
        const cittaStoresList: string = this.props.context.app.config.cittaStoresList || 'Città BLOC,000010|Città Grey Lynn,000012|Città Hamilton,000013|Città Merivale,000016|Città Wellington,000017|Città Wellington Thorndon,000018|Città Newmarket,000041|Città Takapuna,000044';
        const cittaStores: string[] = cittaStoresList.split('|');
        // const colors = this._getDimensionValues(this.props.data.productDimensions || [], this.colorProductDimensionTypeValue);
        // const sizes = this._getDimensionValues(this.props.data.productDimensions || [], this.sizeProductDimensionTypeValue);

        return (
            <div>
                <Modal
                    autoFocus={config.autoFocus}
                    fade={config.fade}
                    isOpen={this.state.isOpen}
                    zIndex={config.zIndex}
                    toggle={this._closeModal}
                    applicationNode={'rsg-root'}
                    className={`enquire-now-modal ${this.state.responseReceived && this.response ? 'success' : ''}`}
                >
                    <ModalHeader toggle={this._closeModal}>{config.modalHeader || 'enquire in-store'}</ModalHeader>
                    <ModalBody>
                        <form className='enquire-form'>
                            {this.state.responseReceived && this.response
                                ? <div className='response-message'>
                                    <p className='response-message-content'>{resources.responseSuccessMessage}</p>
                                </div> : null}
                            {this.state.responseReceived && !this.response ? <div className='response-message'>
                                <p className='response-error-message-content'>{resources.responseErrorMessage}</p>
                            </div> : null}
                            {!this.state.responseReceived ? <div>
                                <div className='enquire-form-design'>
                                    {this.state.dError ? this._renderAlert('contact-form-design') : ''}
                                    <label id='enquire-form-design__label' />
                                    <input
                                        ref={this._designInputRef}
                                        type='text'
                                        onChange={this.handleChange('designInput')}
                                        className='enquire-form-design-text'
                                        aria-label={resources.enquireDesignPlaceholderText}
                                        value={this.state.design}
                                        placeholder={resources.enquireDesignPlaceholderText}
                                        aria-labelledby='enquire-form-design__label enquire-form-design__error'
                                    />
                                </div>
                                <div className='enquire-form-name'>
                                    {this.state.nError ? this._renderAlert('contact-form-name') : ''}
                                    <label id='enquire-form-name__label' />
                                    <input
                                        ref={this._nameInputRef}
                                        type='text'
                                        onChange={this.handleChange('nameInput')}
                                        className='enquire-form-name-text'
                                        aria-label={resources.enquireNamePlaceholderText}
                                        value={this.state.name}
                                        placeholder={resources.enquireNamePlaceholderText}
                                        aria-labelledby='enquire-form-name__label enquire-form-name__error'
                                    />
                                </div>
                                <div className='enquire-form-lastname'>
                                    {this.state.lNError ? this._renderAlert('contact-form-name') : ''}
                                    <label id='enquire-form-lastname__label' />
                                    <input
                                        ref={this._lastNameInputRef}
                                        type='text'
                                        onChange={this.handleChange('lastNameInput')}
                                        className='enquire-form-lastname-text'
                                        aria-label={resources.enquireLastNamePlaceholderText}
                                        value={this.state.lastName}
                                        placeholder={resources.enquireLastNamePlaceholderText}
                                        aria-labelledby='enquire-form-lastname__label enquire-form-lastname__error'
                                    />
                                </div>
                                <div className='enquire-form-emailaddress'>
                                    {this.state.eAError || this.state.eAValError ? this._renderAlert('contact-form-emailaddress') : ''}
                                    <label id='enquire-form-emailaddress__label' />
                                    <input
                                        ref={this._emailAddressInputRef}
                                        type='text'
                                        onChange={this.handleChange('emailAddress')}
                                        className='enquire-form-emailaddress-text'
                                        aria-label={resources.enquireEmailAddressPlaceholderText}
                                        value={this.state.emailAddress}
                                        placeholder={resources.enquireEmailAddressPlaceholderText}
                                        aria-labelledby='enquire-form-emailaddress__label enquire-form-emailaddress__error'
                                    />
                                </div>
                                <div className='enquire-form-phonenumber'>
                                    {this.state.pHError ? this._renderAlert('contact-form-phonenumber') : ''}
                                    <label id='enquire-form-phonenumber__label' />
                                    <input
                                        ref={this._phoneNumberInputRef}
                                        type='tel'
                                        onChange={this.handleChange('phoneNumber')}
                                        className='enquire-form-phonenumber-text'
                                        aria-label={resources.enquirePhoneNumberPlaceholderText}
                                        value={this.state.phoneNumber}
                                        placeholder={resources.enquirePhoneNumberPlaceholderText}
                                        aria-labelledby='enquire-form-phonenumber__label enquire-form-phonenumber__error'
                                    />
                                </div>
                                <div className='storepicker-dropdown'>
                                    <label id='store-picker__label' />
                                    <select
                                        ref={this._storePickerSelectRef}
                                        className='store-picker-text'
                                        value={this.state.storePickerValue}
                                        onChange={this.onChangeSelect}
                                        aria-label='Store Picker'
                                        aria-labelledby='store-picker__label store-picker__error'
                                    >   
                                        {this.props.config.floorStock? <option value={''} key={'default'} >Città Sofa Enquiry</option> : <option value={''} key={'default'} >{'Select Store'}</option>}
                                        {
                                            cittaStores.map((storeData, index) => {
                                                const store = storeData.split(',');
                                                return (
                                                    <option value={store && store[1] || ''} key={index} selected={index === 0 && true || false} aria-selected={index === 0 && true || false} >{store && store[0] || ''}</option>
                                                );
                                            })
                                        }
                                    </select>
                                </div>
                                <div className='enquire-form-upholstery'>
                                    <label id='enquire-form-upholstery__label' />
                                    <input
                                        ref={this._upholsteryInputRef}
                                        type='text'
                                        onChange={this.handleChange('upholstery')}
                                        className='enquire-form-upholstery-text'
                                        aria-label={resources.enquireUpholsteryPlaceholderText}
                                        value={this.state.upholstery}
                                        placeholder={resources.enquireUpholsteryPlaceholderText}
                                        aria-labelledby='enquire-form-upholstery__label enquire-form-upholstery__error'
                                    />
                                </div>
                                {/* {sizes && (
                                    <div className='enquire-customizations'>
                                        <label className='enquire-customizations-heading'>{resources.customisationsLabel}</label>
                                        <div className='enquire-customizations-types'>
                                            {
                                                sizes.map((size, index) => {
                                                    return (
                                                        <div className='c-fauxCheckbox' key={index}>
                                                            <input
                                                                className='fauxRadio'
                                                                type='checkbox'
                                                                aria-checked='false'
                                                                id={size}
                                                                value={size}
                                                                onChange={this.customizationOnChange(size)}
                                                            />
                                                            <label className='fauxRadio' tabIndex={0} htmlFor={size} />
                                                            <label>{size}</label>
                                                        </div>
                                                    );
                                                })
                                            }
                                        </div>
                                        {this.state.cHError ? this._renderAlert('contact-form-customizations') : ''}
                                    </div>
                                )}
                                {colors && (
                                    <div className='enquire-product-type'>
                                        <div className='product-type-dropdown'>
                                            <label id='enquire-product-type__label' />
                                            <select
                                                ref={this._productTypePickerSelectRef}
                                                className='store-picker-text'
                                                value={this.state.productTypePickerValue}
                                                onChange={this.productTypeOnChangeSelect}
                                                aria-label='Enquire Product Type'
                                                aria-labelledby='enquire-product-type__label store-picker__error'
                                            >
                                                {
                                                    colors.map((color, index) => {
                                                        return (
                                                            <option value={color} key={index} selected={index === 0 && true || false} aria-selected={index === 0 && true || false}>{color}</option>
                                                        );
                                                    })
                                                }
                                            </select>
                                        </div>
                                    </div>
                                )} */}
                                <div className='enquire-form-message'>
                                    {this.state.enquiryMessageError ? this._renderAlert('enquire-form-message') : ''}
                                    <label id='enquire-form-message__label' />
                                    <textarea
                                        ref={this._enquiryMessageInputRef}
                                        onChange={this.handleMessageChange}
                                        className='enquire-form-message-text'
                                        aria-label={resources.enquireMessagePlaceholderText}
                                        value={this.state.enquiryMessage}
                                        placeholder={resources.enquireMessagePlaceholderText}
                                        aria-labelledby='enquire-form-message__label enquire-form-message__error'
                                    />
                                </div>
                                <div className='enquire-form-send'>
                                    <Button
                                        className='enquire-form-send-button'
                                        title={resources.enquireSendButtonText}
                                        color='primary'
                                        onClick={this._handleSend}
                                        aria-label={resources.enquireSendButtonText}
                                    >
                                        {resources.enquireSendButtonText}
                                    </Button>

                                    <a href='/stores' target='_blank' role='link' className='store-link'>Store Locations</a>
                                </div>
                            </div>
                                : null}
                        </form>
                    </ModalBody>
                </Modal>
            </div>
        );
    }

     // tslint:disable-next-line: cyclomatic-complexity
     private async _handleSend(e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement> | React.FormEvent<HTMLFormElement>): Promise<void> {
        this.designValue = (this._designInputRef && this._designInputRef.current && this._designInputRef.current.value) ? true : false;
        this.nameValue = (this._nameInputRef && this._nameInputRef.current && this._nameInputRef.current.value) ? true : false;
        this.lastNameValue = (this._lastNameInputRef && this._lastNameInputRef.current && this._lastNameInputRef.current.value) ? true : false;
        this.emailAddressValue = (this._emailAddressInputRef && this._emailAddressInputRef.current && this._emailAddressInputRef.current.value) ? true : false;
        this.phoneNumberValue = (this._phoneNumberInputRef && this._phoneNumberInputRef.current && this._phoneNumberInputRef.current.value) ? true : false;

        const de = (this._designInputRef && this._designInputRef.current && this._designInputRef.current.value) ? this._designInputRef.current.value : '';
        const nA = (this._nameInputRef && this._nameInputRef.current && this._nameInputRef.current.value) ? this._nameInputRef.current.value : '';
        const lN = (this._lastNameInputRef && this._lastNameInputRef.current && this._lastNameInputRef.current.value) ? this._lastNameInputRef.current.value : '';
        const eA = (this._emailAddressInputRef && this._emailAddressInputRef.current && this._emailAddressInputRef.current.value) ? this._emailAddressInputRef.current.value : '';
        const pN = (this._phoneNumberInputRef && this._phoneNumberInputRef.current && this._phoneNumberInputRef.current.value) ? this._phoneNumberInputRef.current.value : '';
        const up = (this._upholsteryInputRef && this._upholsteryInputRef.current && this._upholsteryInputRef.current.value) ? this._upholsteryInputRef.current.value : '';
        const sP = this.props.config.floorStock? 'Città Sofa Enquiry' : this.state.storePickerText;
        const cI = this.props.config.floorStock? 'Città Sofa Enquiry' : this._storePickerSelectRef.current!.value;


        const pT = (this._productTypePickerSelectRef && this._productTypePickerSelectRef.current && this._productTypePickerSelectRef.current.value) ? this._productTypePickerSelectRef.current.value : '';
        const eM = (this._enquiryMessageInputRef && this._enquiryMessageInputRef.current && this._enquiryMessageInputRef.current.value) ? this._enquiryMessageInputRef.current.value : '';
        // const eAVal = this._emailAddressInputRef.current!.value;
        // const pID = this.props.data.product.ItemId || '';
        // const sN = this.state.subcribeNewsletterChecked ? 1 : 0;

        if (!this.designValue) {
            this.setState({
                dError: true,
                reqError: true
            });
        } else if (!this.nameValue) {
            this.setState({
                nError: true,
                reqError: true
            });
        } else if (!this.lastNameValue) {
            this.setState({
                lNError: true,
                reqError: true
            });
        } else if (!this.emailAddressValue) {
            this.setState({
                eAError: true,
                reqError: true
            });
        } else if (!this.isEmailValid(eA)) {
            this.setState({
                eAValError: true
            });
        } else if (!this.phoneNumberValue) {
            this.setState({
                pHError: true,
                reqError: true
            });
        } else {
            this.formInputValues = (this.nameValue && this.lastNameValue && this.emailAddressValue && this.phoneNumberValue) ? true : false;

            if (this.formInputValues && !this.state.eAValError) {
                try {
                    const result = await submitPdpEnquiryFormAction(new SubmitPdpEnquiryFormDataActionInput(nA, lN, eA, pN, sP, de, eM, '', pT, up, 'Enquire Custom Furniture', cI, 0), this.props.context.actionContext);
                    this.response = result.success;

                    //scroll to top of div

                    this.setState({ responseReceived: true });
                } catch {
                    console.log('Please fill the form!');
                }
            }
        }
    }
}

export default CittaEnquireForm;
